import { createContext, useContext, useMemo, useState, PropsWithChildren } from 'react';

export type CardMode = 'light' | 'dark';
export type DashboardDrawerTab = 'askHauser' | 'advisor' | 'tasks';

interface DashboardLayoutContextProps {
  isLeftSidebarCollapsed: boolean;
  isRightSidebarCollapsed: boolean;
  toggleLeftSidebar: () => void;
  toggleRightSidebar: () => void;
  setLeftSidebarCollapsed: (collapsed: boolean) => void;
  setRightSidebarCollapsed: (collapsed: boolean) => void;
  isChatOpen: boolean;
  openChat: () => void;
  openChatWithGroup: (id: string) => void;
  pendingGroupId: string | null;
  clearPendingGroupId: () => void;
  closeChat: () => void;
  isAskHauserOpen: boolean;
  openAskHauser: () => void;
  openAskHauserWithQuestion: (question: string, displayText?: string) => void;
  closeAskHauser: () => void;
  openTasksDrawer: () => void;
  pendingDrawerTabRequest: { tab: DashboardDrawerTab; nonce: number } | null;
  pendingAskHauserQuestion: { prompt: string; displayText?: string } | null;
  clearPendingAskHauserQuestion: () => void;
  pendingInboxRequest: { nonce: number } | null;
  openInbox: () => void;
  clearPendingInboxRequest: () => void;
  cardMode: CardMode;
  toggleCardMode: () => void;
}

export const DashboardLayoutContext = createContext<DashboardLayoutContextProps | null>(null);

export const useDashboardLayout = () => {
  const context = useContext(DashboardLayoutContext);

  if (!context) throw new Error('useDashboardLayout must be used inside DashboardLayoutProvider');

  return context;
};

// The left sidebar starts as the icon rail on a laptop-width window (under 1600px) and open on anything wider, until
// the user picks; their pick is remembered on this device
const SIDEBAR_STORAGE_KEY = 'hauser.sidebarCollapsed';
const LAPTOP_MAX_WIDTH = 1600;

const initialLeftSidebarCollapsed = () => {
  try {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (stored === 'true' || stored === 'false') return stored === 'true';
  } catch {
    // storage blocked: fall through to the width default
  }
  return typeof window !== 'undefined' && window.innerWidth < LAPTOP_MAX_WIDTH;
};

const rememberLeftSidebarCollapsed = (collapsed: boolean) => {
  try {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(collapsed));
  } catch {
    // storage blocked: the choice lasts for this session only
  }
};

export const DashboardLayoutProvider = ({ children }: PropsWithChildren) => {
  const [isLeftSidebarCollapsed, setLeftSidebarCollapsedRaw] = useState(initialLeftSidebarCollapsed);

  const setLeftSidebarCollapsed = (collapsed: boolean) => {
    setLeftSidebarCollapsedRaw(collapsed);
    rememberLeftSidebarCollapsed(collapsed);
  };

  const [isRightSidebarCollapsed, setRightSidebarCollapsed] = useState<boolean>(false);
  // the right panel opens with the app on a computer; on a phone it would cover the whole screen, so it waits to be asked for
  const [isChatOpen, setIsChatOpen] = useState<boolean>(() => typeof window === 'undefined' || window.innerWidth >= 900);
  const [isAskHauserOpen, setIsAskHauserOpen] = useState<boolean>(false);
  const [pendingGroupId, setPendingGroupId] = useState<string | null>(null);
  const [pendingDrawerTabRequest, setPendingDrawerTabRequest] = useState<{
    tab: DashboardDrawerTab;
    nonce: number;
  } | null>(null);
  const [pendingAskHauserQuestion, setPendingAskHauserQuestion] = useState<{ prompt: string; displayText?: string } | null>(null);
  const [pendingInboxRequest, setPendingInboxRequest] = useState<{ nonce: number } | null>(null);
  const [cardMode, setCardMode] = useState<CardMode>('dark');

  const requestDrawerTab = (tab: DashboardDrawerTab) => {
    setPendingDrawerTabRequest((prev) => ({ tab, nonce: (prev?.nonce ?? 0) + 1 }));
  };

  const toggleLeftSidebar = () => {
    setLeftSidebarCollapsed(!isLeftSidebarCollapsed);
  };

  const toggleRightSidebar = () => {
    setRightSidebarCollapsed((prev) => !prev);
  };

  const openChat = () => {
    requestDrawerTab('advisor');
    setIsChatOpen(true);
  };

  const openChatWithGroup = (id: string) => {
    requestDrawerTab('advisor');
    setPendingGroupId(id);
    setIsChatOpen(true);
  };

  const clearPendingGroupId = () => {
    setPendingGroupId(null);
  };

  const closeChat = () => {
    setIsChatOpen(false);
  };

  const openAskHauser = () => {
    requestDrawerTab('askHauser');
    setIsAskHauserOpen(true);
  };

  const openAskHauserWithQuestion = (question: string, displayText?: string) => {
    requestDrawerTab('askHauser');
    setPendingAskHauserQuestion({ prompt: question, displayText });
    setIsAskHauserOpen(true);
  };

  const closeAskHauser = () => {
    setIsAskHauserOpen(false);
  };

  const clearPendingAskHauserQuestion = () => {
    setPendingAskHauserQuestion(null);
  };

  const openTasksDrawer = () => {
    requestDrawerTab('tasks');
    setIsAskHauserOpen(false);
    setIsChatOpen(true);
  };

  const openInbox = () => {
    requestDrawerTab('advisor');
    setPendingInboxRequest((prev) => ({ nonce: (prev?.nonce ?? 0) + 1 }));
    setIsChatOpen(true);
  };

  const clearPendingInboxRequest = () => {
    setPendingInboxRequest(null);
  };

  const toggleCardMode = () => {
    setCardMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const memoizedValue = useMemo(
    () => ({
      isLeftSidebarCollapsed,
      isRightSidebarCollapsed,
      toggleLeftSidebar,
      toggleRightSidebar,
      setLeftSidebarCollapsed,
      setRightSidebarCollapsed,
      isChatOpen,
      openChat,
      openChatWithGroup,
      pendingGroupId,
      clearPendingGroupId,
      closeChat,
      isAskHauserOpen,
      openAskHauser,
      openAskHauserWithQuestion,
      closeAskHauser,
      openTasksDrawer,
      pendingDrawerTabRequest,
      pendingAskHauserQuestion,
      clearPendingAskHauserQuestion,
      pendingInboxRequest,
      openInbox,
      clearPendingInboxRequest,
      cardMode,
      toggleCardMode,
    }),
    [
      isLeftSidebarCollapsed,
      isRightSidebarCollapsed,
      isChatOpen,
      isAskHauserOpen,
      cardMode,
      pendingGroupId,
      pendingDrawerTabRequest,
      pendingAskHauserQuestion,
      pendingInboxRequest,
    ],
  );

  return (
    <DashboardLayoutContext.Provider value={memoizedValue}>
      {children}
    </DashboardLayoutContext.Provider>
  );
};
